import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Flex, FileObjectType, toast } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { WorkflowStep, DATA_TYPE_OPTIONS } from "../../constants";
import { useCrmImportUpload } from "../../hooks";
import { useCrmImportJobDetails } from "../../hooks/useCrmImportQueries";
import { useIdQueryParam } from "../../hooks/useIdQueryParam";
import { FileWithType } from "../../types";
import { getWorkflowRoute } from "../../utils/workflowUtils";
import LoadingOverlay from "../LoadingOverlay";
import ActionButtons from "./ActionButtons";
import ExistingFilesSection from "./ExistingFilesSection";
import FileConfigurationSection from "./FileConfigurationSection";
import FileUploadSection from "./FileUploadSection";
import * as uploadStyles from "./styles_vanilla.css";
import {
  hasExistingFiles,
  extractExistingFiles,
  JOB_CONFIG_CONSTANTS,
  createSortedToolsConfig,
  showDataImportToast,
  DataImportToastType,
} from "./utils";

interface JobInfo {
  arenaDomain: string;
  arenaUserEmail: string;
  environment: string;
  createdAt: string | null;
}

const DataImport: React.FC = () => {
  const history = useHistory();
  const { idFromUrl, createdFromUrl, getQueryParamsWithId } = useIdQueryParam();
  const [filesWithTypes, setFilesWithTypes] = useState<FileWithType[]>([]);
  const [fileUploadKey, setFileUploadKey] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [storedJobInfo, setStoredJobInfo] = useState<JobInfo | null>(null);

  const {
    data: jobDetails,
    loading: jobLoading,
    error: jobError,
    refetch: refetchJobDetails,
  } = useCrmImportJobDetails(idFromUrl || "", {
    skip: !idFromUrl,
  });

  useEffect(() => {
    if (!jobLoading && !jobError && idFromUrl && !jobDetails) {
      toast.error(`The requested job ID "${idFromUrl}" is invalid or does not exist.`);
    }
  }, [jobLoading, jobError, idFromUrl, jobDetails]);

  const jobInfo = jobDetails
    ? {
        arenaDomain: jobDetails.arenaDomain,
        arenaUserEmail: jobDetails.arenaUserEmail,
        environment: jobDetails.environment,
        createdAt: createdFromUrl,
      }
    : null;

  if (jobDetails && !storedJobInfo) {
    setStoredJobInfo(jobInfo);
  }

  const finalJobInfo = storedJobInfo || jobInfo;

  const { uploadFilesToS3, createJob, loading } = useCrmImportUpload();

  // Check if job has existing files
  const jobHasExistingFiles = hasExistingFiles(jobDetails);
  const existingFiles =
    jobHasExistingFiles && jobDetails?.parsedJobConfig
      ? extractExistingFiles(jobDetails.parsedJobConfig)
      : [];

  const allFilesHaveDataTypes = filesWithTypes.every((item) => item.dataType);

  const selectedDataTypes = filesWithTypes
    .map((item) => item.dataType)
    .filter((dataType): dataType is string => Boolean(dataType));

  const handleNext = async () => {
    if (isUploading || loading || !idFromUrl) {
      if (!idFromUrl) {
        showDataImportToast(DataImportToastType.NO_JOB_ID);
      }
      return;
    }

    if (jobLoading) {
      showDataImportToast(DataImportToastType.JOB_LOADING);
      return;
    }

    if (jobError) {
      return;
    }

    if (!finalJobInfo) {
      showDataImportToast(DataImportToastType.JOB_NOT_LOADED);
      return;
    }

    try {
      setIsUploading(true);

      const _uploadResults = await uploadFilesToS3({
        files: filesWithTypes.map((f) => f.file),
        environment: finalJobInfo.environment,
        jobType: JOB_CONFIG_CONSTANTS.JOB_TYPE,
        arenaDomain: finalJobInfo.arenaDomain,
        arenaUserEmail: finalJobInfo.arenaUserEmail,
      });

      // Create tools configuration and sort by predefined order
      const tools = createSortedToolsConfig(filesWithTypes, {
        arenaDomain: finalJobInfo.arenaDomain,
        arenaUserEmail: finalJobInfo.arenaUserEmail,
      });

      // Update the existing job with file information using fetched job details
      const jobResult = await createJob({
        jobType: JOB_CONFIG_CONSTANTS.JOB_TYPE,
        arenaDomain: finalJobInfo.arenaDomain,
        arenaUserEmail: finalJobInfo.arenaUserEmail,
        env: finalJobInfo.environment,
        jobId: idFromUrl,
        jobConfig: {
          tools,
        },
        substate: JOB_CONFIG_CONSTANTS.SUBSTATE,
        ...(finalJobInfo.createdAt && { createdAt: finalJobInfo.createdAt }),
      });

      if (jobResult.success) {
        // Navigate to validation page with the same job ID
        history.push(getWorkflowRoute(WorkflowStep.DATA_VALIDATION, getQueryParamsWithId()));

        showDataImportToast(DataImportToastType.UPLOAD_SUCCESS);
      } else {
        throw new Error(jobResult.message || "Failed to update job with file information");
      }
    } catch (error) {
      showDataImportToast(DataImportToastType.UPLOAD_ERROR, error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFilesSelected = (selectedFiles: FileObjectType[]) => {
    const acceptedFiles = selectedFiles.filter((file) => file.status === "accepted");

    setFilesWithTypes((prevFilesWithTypes) => {
      const updatedFiles = [...prevFilesWithTypes];

      acceptedFiles.forEach((newFile) => {
        const existingIndex = updatedFiles.findIndex(
          (existingItem) => existingItem.file.file.name === newFile.file.name,
        );

        if (existingIndex >= 0) {
          updatedFiles[existingIndex] = {
            file: newFile,
            dataType: updatedFiles[existingIndex].dataType,
          };
        } else {
          updatedFiles.push({
            file: newFile,
            dataType: undefined,
          });
        }
      });

      return updatedFiles;
    });
  };

  const handleRemoveFile = (fileName: string) => {
    setFilesWithTypes((prevFilesWithTypes) =>
      prevFilesWithTypes.filter((item) => item.file.file.name !== fileName),
    );
    setFileUploadKey((prev) => prev + 1);
  };

  const handleDataTypeChange = (
    fileName: string,
    dataType: string | number | string[] | number[],
  ) => {
    setFilesWithTypes((prevFilesWithTypes) =>
      prevFilesWithTypes.map((item) =>
        item.file.file.name === fileName ? { ...item, dataType: String(dataType) } : item,
      ),
    );
  };

  const isDataTypeDisabled = (dataTypeValue: string, currentFileDataType?: string) => {
    return selectedDataTypes.includes(dataTypeValue) && currentFileDataType !== dataTypeValue;
  };

  const getAvailableDataTypes = (currentFileDataType?: string) => {
    const allDataTypes = DATA_TYPE_OPTIONS;

    return allDataTypes.filter(
      (dataType) => !isDataTypeDisabled(dataType.value, currentFileDataType),
    );
  };

  return (
    <LoadingOverlay
      loading={jobLoading && !!idFromUrl}
      error={jobError && idFromUrl ? jobError : null}
      message="Loading job details..."
      onRetry={() => refetchJobDetails()}
      onCancel={() => history.push("/data-studio")}
      cancelText="Back to Data Studio"
    >
      <Flex flexDirection="column" gap={Sizes.LG} className={uploadStyles.outerContainer}>
        <Flex flexDirection="column" className={uploadStyles.mainUploadContainer}>
          <Flex flexDirection="column" gap={Sizes.LG}>
            {jobHasExistingFiles ? (
              <ExistingFilesSection existingFiles={existingFiles} />
            ) : (
              <>
                <FileUploadSection
                  fileUploadKey={fileUploadKey}
                  onFileSelected={handleFilesSelected}
                />

                <FileConfigurationSection
                  filesWithTypes={filesWithTypes}
                  getAvailableDataTypes={getAvailableDataTypes}
                  onDataTypeChange={handleDataTypeChange}
                  onRemoveFile={handleRemoveFile}
                />
              </>
            )}
          </Flex>
        </Flex>

        <ActionButtons
          filesWithTypes={filesWithTypes}
          allFilesHaveDataTypes={allFilesHaveDataTypes}
          onNext={handleNext}
          isUploading={isUploading || loading || jobLoading}
          hasExistingFiles={jobHasExistingFiles}
        />
      </Flex>
    </LoadingOverlay>
  );
};

export default DataImport;
